import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Plus } from 'lucide-react';
import PageShell from '../../components/PageShell';
import PillFilter from '../../components/PillFilter';
import SegmentedToggle from '../../components/SegmentedToggle';
import { getGovernanceJournal, putGovernanceJournal } from '../../api/tenants';
import { BackHome } from '../WelcomeNewsPage';
import {
  DECISION_STATUSES, NOTE_TAGS, TAG_MAX, newTag, normNote, parseJournal, parseTags, removeTag,
  serializeJournal, sortDecisions, sortNotes, tagKey, todayISO, withDecisionDate, withDue,
} from './journalModel';
import '../WelcomePage.css';
import './JournalPage.css';

/* JOURNAL ET DÉCISIONS (refonte du 2026-10-01, prototype de Codex).
 *
 * Deux vues d'UNE même liste de notes : le Journal les montre toutes, filtrées
 * par étiquette ; les Décisions ne montrent que celles qu'on SUIT (responsable,
 * échéance, statut). Une note se suit depuis le journal, une décision se
 * retire du suivi sans disparaître du journal. La saisie se fait dans une
 * carte posée au-dessus de la liste, pas dans une fenêtre.
 *
 * Le document s'enregistre à chaque geste, comme avant ; la vue ouverte vient
 * de la section du menu (`view`). */

const DUE_FMT = { day: 'numeric', month: 'long' };
function fmtDay(iso, withYear = false) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-CH', withYear ? { ...DUE_FMT, year: 'numeric' } : DUE_FMT);
}

/* AJOUTER UNE ÉTIQUETTE À LA NOTE (2026-10-09) : on tape, les étiquettes du
   client qui correspondent se proposent d'abord ; « Créer » n'apparaît que si
   aucune ne porte déjà ce nom (sans tenir compte des accents ni de la casse),
   ce qui évite les doublons sans rien interdire. */
function TagAdder({ all, picked, labelOf, onPick, onCreate, n }) {
  const [q, setQ] = useState('');
  const key = tagKey(q);
  const exact = key ? all.find((tg) => tagKey(labelOf(tg)) === key) : null;
  const hits = key ? all.filter((tg) => !picked.includes(tg.id) && tagKey(labelOf(tg)).includes(key)) : [];
  const take = () => {
    if (!key) return;
    if (exact) { if (!picked.includes(exact.id)) onPick(exact.id); } else onCreate(q);
    setQ('');
  };
  return (
    <div className="jn-tagadd">
      <input className="lk-filter-input" value={q} maxLength={TAG_MAX} placeholder={n('newTag')}
             aria-label={n('newTag')} onChange={(e) => setQ(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); take(); } }} />
      {hits.map((tg) => (
        <button key={tg.id} type="button" className="jn-tagadd__hit" onClick={() => { onPick(tg.id); setQ(''); }}>
          {labelOf(tg)}
        </button>
      ))}
      {key && !exact && (
        <button type="button" className="jn-tagadd__new" onClick={take}>
          <Plus size={13} strokeWidth={2} aria-hidden="true" /> {n('createTag', { label: q.trim() })}
        </button>
      )}
    </div>
  );
}

/* Une ligne de la liste des étiquettes : on renomme sur place (Entrée ou en
   quittant le champ, Échap rend le nom), on supprime d'un clic. */
function TagRow({ tag, label, count, taken, onRename, onDelete, n }) {
  const [v, setV] = useState(label);
  useEffect(() => { setV(label); }, [label]);
  const save = () => {
    const next = v.trim();
    if (!next || next === label || taken(next, tag.id)) { setV(label); return; }
    onRename(next);
  };
  return (
    <div className="jn-tagman__row">
      <input className="lk-filter-input" value={v} maxLength={TAG_MAX} aria-label={label}
             onChange={(e) => setV(e.target.value)} onBlur={save}
             onKeyDown={(e) => {
               if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
               if (e.key === 'Escape') { setV(label); }
             }} />
      <span className="jn-tagman__n">{n('tagUse', { count })}</span>
      <button type="button" className="wch-textlink jn-tagman__del" onClick={onDelete}>{n('deleteTag')}</button>
    </div>
  );
}

/* Une décision DATÉE est prise : elle n'est plus « à valider ». Son
   échéance ne la précède jamais (2026-10-09). */
const statusOptions = (dated, n) => DECISION_STATUSES.map((s) => ({
  value: s, label: n(`statusLabel.${s}`),
  disabled: s === 'to_validate' && dated,
  title: s === 'to_validate' && dated ? n('toValidateDated') : undefined,
}));

function NoteEditor({ draft, onChange, onSave, onCancel, onDelete, n, inline = false, tags, labelOf }) {
  const set = (patch) => onChange({ ...draft, ...patch });
  /* Ouverte À SA PLACE dans la liste (2026-10-09), la saisie se montre :
     elle ne se perd pas hors de l'écran. */
  const ref = useRef(null);
  useEffect(() => {
    if (inline && ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ block: 'nearest' });
  }, [inline]);
  const toggleTag = (tag) => set({ tags: draft.tags.includes(tag) ? draft.tags.filter((x) => x !== tag) : [...draft.tags, tag] });
  /* Les étiquettes du client, plus celles créées dans cette saisie : elles
     n'entrent dans la liste qu'à l'enregistrement de la note. */
  const allTags = [...tags, ...(draft.created || [])];
  const tracked = !!draft.decision;
  const setDecision = (patch) => set({ decision: { ...(draft.decision || { owner: '', date: '', due: '', status: 'todo' }), ...patch } });
  // L'échéance n'est plus obligatoire (2026-10-03) : un responsable suffit.
  const valid = draft.title.trim() && (!tracked || draft.decision.owner.trim());
  const setDate = (date) => set({ decision: withDecisionDate(draft.decision, date) });
  const setDue = (due) => set({ decision: withDue(draft.decision, due) });
  return (
    <form ref={ref} className={`jn-form${inline ? ' jn-form--inline' : ''}`}
          onSubmit={(e) => { e.preventDefault(); if (valid) onSave(); }}>
      <h3>{draft.isNew ? n('newNote') : n('editNote')}</h3>
      <label className="jn-field">
        <span>{n('title')}</span>
        <input className="lk-filter-input" required maxLength={160} value={draft.title}
               placeholder={n('titlePlaceholder')} onChange={(e) => set({ title: e.target.value })} />
      </label>
      <label className="jn-field">
        <span>{n('note')}</span>
        <textarea className="lk-filter-input" rows={3} value={draft.text}
                  onChange={(e) => set({ text: e.target.value })} />
      </label>
      <fieldset className="jn-tags">
        <legend>{n('tags')}</legend>
        {allTags.map((tg) => (
          <label key={tg.id}>
            <input type="checkbox" checked={draft.tags.includes(tg.id)} onChange={() => toggleTag(tg.id)} />
            {labelOf(tg)}
          </label>
        ))}
        <TagAdder all={allTags} picked={draft.tags} labelOf={labelOf} n={n}
                  onPick={(id) => set({ tags: [...draft.tags, id] })}
                  onCreate={(label) => {
                    const tg = newTag(label);
                    set({ created: [...(draft.created || []), tg], tags: [...draft.tags, tg.id] });
                  }} />
      </fieldset>
      <label className="jn-check">
        <input type="checkbox" checked={tracked}
               onChange={(e) => set({ decision: e.target.checked ? { owner: '', date: '', due: '', status: 'todo' } : null })} />
        {n('trackAsDecision')}
      </label>
      {tracked && (
        <div className="jn-decision">
          <label className="jn-field">
            <span>{n('owner')}</span>
            <input className="lk-filter-input" required value={draft.decision.owner}
                   onChange={(e) => setDecision({ owner: e.target.value })} />
          </label>
          <label className="jn-field">
            <span>{n('decisionDate')}</span>
            <input className="lk-filter-input" type="date" value={draft.decision.date || ''}
                   onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="jn-field">
            <span>{n('due')}</span>
            <input className="lk-filter-input" type="date" value={draft.decision.due || ''}
                   min={draft.decision.date || undefined}
                   onChange={(e) => setDue(e.target.value)} />
          </label>
          <div className="jn-field">
            <span>{n('status')}</span>
            <SegmentedToggle compact value={draft.decision.status} onChange={(v) => setDecision({ status: v })}
                             options={statusOptions(!!draft.decision.date, n)} />
          </div>
        </div>
      )}
      <div className="jn-form__actions">
        <button type="submit" className="wch-btn wch-btn--primary" disabled={!valid}>{n('save')}</button>
        <button type="button" className="wch-btn" onClick={onCancel}>{n('cancel')}</button>
        {!draft.isNew && onDelete && (
          <button type="button" className="wch-textlink jn-delete" onClick={onDelete}>{n('delete')}</button>
        )}
      </div>
    </form>
  );
}

export default function JournalPage({ activeTenant, view = 'journal', author = '', canWrite = true,
                                      setPage, onGoCockpitTab }) {
  const { t } = useTranslation();
  const n = (k, o) => t(`cockpit:notes.${k}`, o);
  const tid = activeTenant?.id || null;
  const [notes, setNotes] = useState([]);
  const [tags, setTags] = useState([]);
  const [managing, setManaging] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [tagFilter, setTagFilter] = useState('all');
  const [decFilter, setDecFilter] = useState('open');
  const [draft, setDraft] = useState(null);
  const lastSaved = useRef('');

  useEffect(() => {
    if (!tid) return undefined;
    let alive = true;
    setLoaded(false);
    getGovernanceJournal(tid).then((res) => {
      if (!alive) return;
      const data = res && res.data ? res.data.data : null;
      const list = parseJournal(data);
      const tg = parseTags(data);
      setNotes(list);
      setTags(tg);
      lastSaved.current = JSON.stringify(serializeJournal(list, tg));
      setLoaded(true);
    }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [tid]);

  /* Une saisie en cours ne survit pas au changement de vue, sauf quand on
     l'y emmène (« Voir dans le journal »). */
  const carry = useRef(false);
  useEffect(() => {
    if (carry.current) { carry.current = false; return; }
    setDraft(null);
  }, [view]);

  async function commit(next, nextTags = tags) {
    setNotes(next);
    setTags(nextTags);
    const doc = serializeJournal(next, nextTags);
    const raw = JSON.stringify(doc);
    if (!tid || raw === lastSaved.current) return;
    // Un refus remonte au message d'erreur global ; la saisie reste à l'écran.
    const res = await putGovernanceJournal(tid, doc);
    if (res && !res.error) lastSaved.current = raw;
  }

  const startNew = (decision) => setDraft({
    isNew: true, id: null, title: '', text: '', tags: [],
    decision: decision ? { owner: '', date: '', due: '', status: 'todo' } : null,
  });
  const startEdit = (note, patch = {}) => setDraft({ ...JSON.parse(JSON.stringify(note)), isNew: false, ...patch });
  const saveDraft = () => {
    const { isNew, created = [], ...rest } = draft;
    const clean = { ...rest, title: rest.title.trim(), text: rest.text.trim() };
    // Une étiquette créée puis décochée dans la saisie ne s'enregistre pas.
    const nextTags = [...tags, ...created.filter((tg) => clean.tags.includes(tg.id))];
    if (isNew) {
      commit([...notes, normNote({ ...clean, id: undefined, date: todayISO(), author })], nextTags);
      if (view === 'journal' && tagFilter !== 'all' && !clean.tags.includes(tagFilter)) setTagFilter('all');
    } else {
      commit(notes.map((x) => (x.id === clean.id ? normNote({ ...x, ...clean }) : x)), nextTags);
    }
    setDraft(null);
  };
  const remove = (id) => { commit(notes.filter((x) => x.id !== id)); setDraft(null); };
  const setStatus = (id, status) => commit(notes.map((x) => (x.id === id && x.decision
    ? { ...x, decision: { ...x.decision, status } } : x)));
  const untrack = (id) => { commit(notes.map((x) => (x.id === id ? { ...x, decision: null } : x))); setDraft(null); };
  const goJournal = (note) => {
    carry.current = true;
    setTagFilter('all');
    startEdit(note);
    if (onGoCockpitTab) onGoCockpitTab('journal');
  };

  const journalItems = useMemo(
    () => sortNotes(notes).filter((x) => tagFilter === 'all' || x.tags.includes(tagFilter)),
    [notes, tagFilter],
  );
  const decisionItems = useMemo(() => sortDecisions(notes).filter((x) => (decFilter === 'all'
    || (decFilter === 'done' ? x.decision.status === 'done' : x.decision.status !== 'done'))), [notes, decFilter]);

  /* Le nom d'une étiquette : celui que le client lui a donné, sinon, pour les
     cinq de départ, leur libellé traduit. */
  const labelOf = (tg) => tg.label || (NOTE_TAGS.includes(tg.id) ? n(`tag.${tg.id}`) : tg.id);
  const tagById = new Map(tags.map((tg) => [tg.id, tg]));
  const tagChips = (note) => (note.tags.length > 0 && (
    <div className="jn-chips">
      {note.tags.filter((id) => tagById.has(id)).map((id) => <span key={id}>{labelOf(tagById.get(id))}</span>)}
    </div>
  ));
  const editor = (inline) => draft && canWrite && (
    <NoteEditor draft={draft} onChange={setDraft} onSave={saveDraft} onCancel={() => setDraft(null)}
                onDelete={draft.id ? () => remove(draft.id) : null} n={n} inline={inline}
                tags={tags} labelOf={labelOf} />
  );

  /* GÉRER LES ÉTIQUETTES (2026-10-09) : renommer vaut pour toutes les notes,
     supprimer retire l'étiquette des notes qui la portaient. */
  const taken = (label, exceptId) => tags.some((tg) => tg.id !== exceptId && tagKey(labelOf(tg)) === tagKey(label));
  const renameTag = (id, label) => commit(notes, tags.map((tg) => {
    if (tg.id !== id) return tg;
    // Un nom de départ retapé tel quel redevient traduit.
    return { ...tg, label: NOTE_TAGS.includes(id) && label === n(`tag.${id}`) ? '' : label };
  }));
  const deleteTag = (id) => {
    const count = notes.filter((x) => x.tags.includes(id)).length;
    if (count && !window.confirm(n('deleteTagConfirm', { count, label: labelOf(tagById.get(id)) }))) return;
    const next = removeTag(notes, tags, id);
    commit(next.notes, next.tags);
    if (tagFilter === id) setTagFilter('all');
  };
  const [newLabel, setNewLabel] = useState('');
  const addTag = () => {
    const label = newLabel.trim();
    if (!label || taken(label)) return;
    commit(notes, [...tags, newTag(label)]);
    setNewLabel('');
  };
  const tagManager = managing && canWrite && (
    <div className="jn-form jn-tagman">
      <h3>{n('tags')}</h3>
      {tags.map((tg) => (
        <TagRow key={tg.id} tag={tg} label={labelOf(tg)} n={n} taken={taken}
                count={notes.filter((x) => x.tags.includes(tg.id)).length}
                onRename={(label) => renameTag(tg.id, label)} onDelete={() => deleteTag(tg.id)} />
      ))}
      <div className="jn-tagman__row">
        <input className="lk-filter-input" value={newLabel} maxLength={TAG_MAX} placeholder={n('newTag')}
               aria-label={n('newTag')} onChange={(e) => setNewLabel(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
        <button type="button" className="wch-btn" onClick={addTag}
                disabled={!newLabel.trim() || taken(newLabel)}>{n('addTag')}</button>
      </div>
      <div className="jn-form__actions">
        <button type="button" className="wch-btn" onClick={() => setManaging(false)}>{n('closeTags')}</button>
      </div>
    </div>
  );
  /* Une note qu'on modifie s'ouvre À SA PLACE (2026-10-09) ; seule une note
     nouvelle, ou absente de la liste affichée, s'ouvre en tête. */
  const shown = view !== 'decisions' ? journalItems : decisionItems;
  const inPlace = !!(draft && !draft.isNew && shown.some((x) => x.id === draft.id));
  const editing = (note) => inPlace && draft.id === note.id;

  const isJournal = view !== 'decisions';
  return (
    <PageShell title={isJournal ? t('cockpit:tabs.journal') : t('cockpit:tabs.decisions')}
               actions={<BackHome setPage={setPage} />}>
      <div className="wch">
        <div className="jn-bar">
          {isJournal ? (
            <div className="jn-bar__tags">
              <PillFilter multiple={false} value={tagFilter} onChange={(v) => { setTagFilter(v); setDraft(null); }}
                          options={[{ value: 'all', label: n('allNotes') },
                            ...tags.map((tg) => ({ value: tg.id, label: labelOf(tg) }))]} />
              {canWrite && (
                <button type="button" className="wch-textlink" onClick={() => setManaging((x) => !x)}>
                  {n('manageTags')}
                </button>
              )}
            </div>
          ) : (
            <PillFilter multiple={false} value={decFilter} onChange={(v) => { setDecFilter(v); setDraft(null); }}
                        options={[{ value: 'open', label: n('filterOpen') }, { value: 'done', label: n('filterDone') },
                          { value: 'all', label: n('filterAll') }]} />
          )}
          {canWrite && (
            <button type="button" className="wch-btn wch-btn--primary" onClick={() => startNew(!isJournal)}>
              <Plus size={14} strokeWidth={2} aria-hidden="true" /> {isJournal ? n('addNote') : n('addDecision')}
            </button>
          )}
        </div>

        {isJournal && tagManager}
        {!inPlace && editor(false)}

        {isJournal && loaded && (
          <div className="wch-list jn-list">
            {journalItems.length === 0 && <p className="wch-empty jn-empty">{tagFilter === 'all' ? n('emptyJournal') : n('emptyTag')}</p>}
            {journalItems.map((note) => (editing(note) ? <React.Fragment key={note.id}>{editor(true)}</React.Fragment> : (
              <article className="wch-entry" key={note.id}>
                <div className="wch-entry__date">
                  {fmtDay(note.date, note.date.slice(0, 4) !== todayISO().slice(0, 4))}
                  {note.author && <span>{note.author}</span>}
                </div>
                <div className="wch-entry__copy">
                  {tagChips(note)}
                  <h3>{note.title}</h3>
                  {note.text && <p className="jn-text">{note.text}</p>}
                  {note.decision && (
                    <div className="jn-tracking">
                      {[n('decision'), note.decision.owner,
                        note.decision.date ? `${n('decidedOn')} ${fmtDay(note.decision.date, true)}` : null,
                        note.decision.due ? `${n('dueOn')} ${fmtDay(note.decision.due)}` : null,
                        n(`statusLabel.${note.decision.status}`)].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {canWrite && (
                    <div className="jn-actions">
                      <button type="button" className="wch-textlink" onClick={() => startEdit(note)}>{n('editNoteTags')}</button>
                      <button type="button" className="wch-textlink"
                              onClick={() => startEdit(note, note.decision ? {} : { decision: { owner: '', date: '', due: '', status: 'todo' } })}>
                        {note.decision ? n('editTracking') : n('track')} <ArrowRight size={12} strokeWidth={1.8} aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              </article>
            )))}
          </div>
        )}

        {!isJournal && loaded && (
          <div className="jn-decisions">
            {decisionItems.length === 0 && <p className="wch-empty">{n('emptyDecisions')}</p>}
            {decisionItems.map((note) => (editing(note) ? <React.Fragment key={note.id}>{editor(true)}</React.Fragment> : (
              <article className="jn-dec" key={note.id}>
                {tagChips(note)}
                <div className="jn-dec__top">
                  <h3>{note.title}</h3>
                  <select className="lk-filter-select" aria-label={n('statusOf', { title: note.title })}
                          value={note.decision.status} disabled={!canWrite}
                          onChange={(e) => setStatus(note.id, e.target.value)}>
                    {statusOptions(!!note.decision.date, n).map((o) => (
                      <option key={o.value} value={o.value} disabled={o.disabled} title={o.title}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {note.text && <p className="jn-text">{note.text}</p>}
                <div className="jn-dec__meta">
                  <span>{n('owner')}<strong>{note.decision.owner || '—'}</strong></span>
                  <span>{n('decisionDate')}<strong>{note.decision.date ? fmtDay(note.decision.date, true) : '—'}</strong></span>
                  <span>{n('due')}<strong>{note.decision.due ? fmtDay(note.decision.due) : n('noDue')}</strong></span>
                </div>
                <div className="jn-actions">
                  {canWrite && (
                    <button type="button" className="wch-textlink" onClick={() => startEdit(note)}>{n('editNoteTracking')}</button>
                  )}
                  <button type="button" className="wch-textlink" onClick={() => goJournal(note)}>
                    {n('seeInJournal')} <ArrowRight size={12} strokeWidth={1.8} aria-hidden="true" />
                  </button>
                  {canWrite && (
                    <button type="button" className="wch-textlink" onClick={() => untrack(note.id)}>{n('untrack')}</button>
                  )}
                </div>
              </article>
            )))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
